import { useState, useEffect, useRef } from 'react';
import { Shield, Building2, User, Upload, CheckCircle, FileText, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { getNDAText, submitNDA } from '@/lib/api';

const AccessPage = () => {
  const [ndaText, setNdaText] = useState('');
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [hasAcceptedNDA, setHasAcceptedNDA] = useState(false);
  const [applicantType, setApplicantType] = useState('company');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const scrollRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    country: '',
    companyName: '',
  });

  const [files, setFiles] = useState({
    companyRegistration: null,
    signatoryId: null,
    passport: null,
  });

  useEffect(() => {
    // Add noindex meta tag
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    // Fetch NDA text
    getNDAText()
      .then((res) => setNdaText(res.data.text))
      .catch(() => toast.error('Failed to load NDA text'));

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if user has scrolled to within 100px of the bottom
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    if (isAtBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  };

  // Also check on initial load if content is short enough to not need scrolling
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      const { scrollHeight, clientHeight } = scrollContainer;
      // If content doesn't need scrolling, enable checkbox immediately
      if (scrollHeight <= clientHeight + 10) {
        setHasScrolledToEnd(true);
      }
    }
  }, [ndaText]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PDF, JPG, or PNG files only.');
        return;
      }
      setFiles({ ...files, [fileType]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasAcceptedNDA) {
      toast.error('Please accept the NDA to continue');
      return;
    }

    if (!formData.fullName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (applicantType === 'company') {
      if (!formData.companyName) {
        toast.error('Company name is required');
        return;
      }
      if (!files.companyRegistration || !files.signatoryId) {
        toast.error('Please upload all required documents');
        return;
      }
    } else {
      if (!files.passport) {
        toast.error('Please upload your passport copy');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('applicant_type', applicantType);
      submitData.append('full_name', formData.fullName);
      submitData.append('email', formData.email);
      if (formData.country) submitData.append('country', formData.country);
      if (formData.companyName) submitData.append('company_name', formData.companyName);

      if (applicantType === 'company') {
        submitData.append('company_registration', files.companyRegistration);
        submitData.append('signatory_id', files.signatoryId);
      } else {
        submitData.append('passport', files.passport);
      }

      await submitNDA(submitData);
      setIsSubmitted(true);
      toast.success('NDA submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-lg w-full glass-card animate-fade-in">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
              Submission Received
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Thank you for your submission. Our team will review your documents and contact you shortly with your access credentials.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-4">
          <img 
            src="/softmaya-logo.png" 
            alt="Softmaya" 
            className="h-10 w-auto"
          />
          <div>
            <p className="text-sm text-muted-foreground">NDA & Access Request</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* NDA Section */}
          <div className="space-y-6">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-foreground mb-2">
                Non-Disclosure Agreement
              </h2>
              <p className="text-muted-foreground">
                Please read the agreement carefully and scroll to the end to accept.
              </p>
            </div>

            <Card className="glass-card">
              <CardContent className="p-0">
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="h-[500px] overflow-y-auto p-6 nda-scroll-container"
                  data-testid="nda-scroll-container"
                >
                  <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground leading-relaxed">
                    {ndaText}
                  </pre>
                </div>
                {!hasScrolledToEnd && (
                  <div className="border-t border-border p-4 flex items-center justify-center gap-2 text-muted-foreground bg-card/50">
                    <ChevronDown className="w-4 h-4 animate-bounce" />
                    <span className="text-sm">Scroll to read the full agreement</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-nda"
                checked={hasAcceptedNDA}
                onCheckedChange={setHasAcceptedNDA}
                disabled={!hasScrolledToEnd}
                className="mt-1"
                data-testid="accept-nda-checkbox"
              />
              <Label
                htmlFor="accept-nda"
                className={`text-sm leading-relaxed ${
                  hasScrolledToEnd ? 'text-foreground cursor-pointer' : 'text-muted-foreground cursor-not-allowed'
                }`}
              >
                I have read, understood, and agree to the Softmaya Non-Disclosure Agreement.
              </Label>
            </div>
          </div>

          {/* Form Section */}
          <div className="space-y-6">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-foreground mb-2">
                Your Information
              </h2>
              <p className="text-muted-foreground">
                Complete the form below to submit your access request.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Applicant Type */}
              <Card className="glass-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base font-medium">Applicant Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={applicantType}
                    onValueChange={setApplicantType}
                    className="flex gap-4"
                    data-testid="applicant-type-radio"
                  >
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => setApplicantType('company')}
                    >
                      <div
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                          applicantType === 'company'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <RadioGroupItem value="company" id="company" data-testid="radio-company" />
                        <Building2 className="w-5 h-5 text-primary" />
                        <span>Company</span>
                      </div>
                    </div>
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => setApplicantType('individual')}
                    >
                      <div
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                          applicantType === 'individual'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <RadioGroupItem value="individual" id="individual" data-testid="radio-individual" />
                        <User className="w-5 h-5 text-primary" />
                        <span>Individual</span>
                      </div>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Card className="glass-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base font-medium">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        className="bg-background/50"
                        data-testid="full-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@company.com"
                        required
                        className="bg-background/50"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        placeholder="United Kingdom"
                        className="bg-background/50"
                        data-testid="country-input"
                      />
                    </div>
                    {applicantType === 'company' && (
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="Acme Corporation"
                          required
                          className="bg-background/50"
                          data-testid="company-name-input"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Document Upload */}
              <Card className="glass-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base font-medium">Document Upload</CardTitle>
                  <CardDescription>
                    {applicantType === 'company'
                      ? 'Upload your company registration and authorized signatory ID'
                      : 'Upload a copy of your passport'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {applicantType === 'company' ? (
                    <>
                      <div className="space-y-2">
                        <Label>Company Registration Document *</Label>
                        <div className="relative">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'companyRegistration')}
                            className="bg-background/50 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary/10 file:text-primary file:font-medium"
                            data-testid="company-registration-input"
                          />
                          {files.companyRegistration && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-primary">
                              <FileText className="w-4 h-4" />
                              {files.companyRegistration.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Authorized Signatory ID/Passport *</Label>
                        <div className="relative">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'signatoryId')}
                            className="bg-background/50"
                            data-testid="signatory-id-input"
                          />
                          {files.signatoryId && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-primary">
                              <FileText className="w-4 h-4" />
                              {files.signatoryId.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label>Passport Copy *</Label>
                      <div className="relative">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'passport')}
                          className="bg-background/50"
                          data-testid="passport-input"
                        />
                        {files.passport && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-primary">
                            <FileText className="w-4 h-4" />
                            {files.passport.name}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Accepted formats: PDF, JPG, PNG (Max 10MB)
                  </p>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={!hasAcceptedNDA || isSubmitting}
                className="w-full gold-glow-hover transition-all duration-300"
                data-testid="submit-nda-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Submit NDA Request
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccessPage;
